<script setup>
import { computed, nextTick, watch } from 'vue'
import { useRoute } from 'vue-router'
import DssNewHeader from '@/core/components/dss-new-header/dss-new-header.vue'
import DssSidebar from '@/core/components/dss-sidebar/dss-sidebar.vue'
import { useLayoutWidth } from '@/core/composables/use-layout-width.composable'
import { useLanguageStore } from '@/core/stores/language.store.js'
import { useUIStore } from '@/core/stores/ui.store'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import BradleyFloatingWidget from '@/modules/new-bradley/components/bradley-floating-widget.vue'
import BradleySidePanel from '@/modules/new-bradley/components/bradley-side-panel.vue'
import DssBreadcrumb from '@/shared/components/dss-breadcrumb/dss-breadcrumb.vue'

defineOptions({
  name: 'HomeLayout',
})

const route = useRoute()
const uiStore = useUIStore()
const authStore = useAuthStore()
const languageStore = useLanguageStore()

// Primary trigger: fires as soon as tenantData is available (direct reactive dep)
watch(
  () => authStore.tenantData,
  async (tenantData) => {
    if (tenantData) {
      await languageStore.fetchAvailableLocales()
      languageStore.initialize()
    }
  },
  { immediate: true },
)

// Secondary trigger: re-validates if tenant language settings change mid-session
watch(
  () => languageStore.getActiveLocales.map(l => l.key).join(','),
  () => languageStore.initialize(),
)

const lockMainScroll = computed(() =>
  route.matched.some(record => record.meta?.embedFullViewport === true),
)

const { isFullWidth } = useLayoutWidth()

watch(
  [isFullWidth, () => uiStore.fullscreenMode],
  async () => {
    await nextTick()
    window.dispatchEvent(new Event('resize'))
  },
)
</script>

<template>
  <div
    class="grid min-w-0 font-inter bg-white dark:bg-dss-surface-0"
    :class="[
      lockMainScroll
        ? 'h-dvh max-h-dvh grid-rows-[auto_minmax(0,1fr)] overflow-hidden'
        : 'min-h-screen grid-rows-[auto_1fr]',
      isFullWidth ? '' : 'home-layout-container',
    ]"
  >
    <dss-new-header v-show="!uiStore.fullscreenMode" />

    <div
      class="grid min-h-0 min-w-0"
      :class="[
        uiStore.fullscreenMode ? 'grid-cols-[1fr]' : 'grid-cols-[auto_1fr]',
        lockMainScroll ? 'overflow-hidden' : '',
      ]"
    >
      <dss-sidebar v-show="!uiStore.fullscreenMode" />

      <div
        class="flex min-w-0 flex-col"
        :class="lockMainScroll ? 'min-h-0 overflow-hidden' : ''"
      >
        <dss-breadcrumb v-show="!uiStore.fullscreenMode" />

        <div
          id="home-layout-content"
          class="relative flex flex-1 flex-col min-h-0 min-w-0 bg-dss-surface-10"
          :class="
            lockMainScroll
              ? 'gap-0 overflow-hidden p-0'
              : 'gap-6 p-4 sm:gap-8 sm:p-8 overflow-y-auto'
          "
        >
          <router-view />
        </div>

        <footer
          v-if="!lockMainScroll"
          class="border-t border-dss-surface-200 bg-dss-surface-10 px-4 sm:px-8 py-4 text-center text-sm text-dss-surface-700 dark:text-dss-surface-500"
        >
          {{ $t('footer.copyright') }}
        </footer>
      </div>
    </div>
  </div>
  <bradley-side-panel />
  <bradley-floating-widget />
</template>

<style scoped>
.home-layout-container {
  margin-left: auto;
  margin-right: auto;
}

@media (min-width: 768px) {
  .home-layout-container {
    max-width: min(1440px, calc(100% - 64px));
  }
}
</style>
